<template>
  <div class="toolbar">
    <div class="flex center-items" style="margin-left: -2em;">
      <img alt="Sphere Logo" src="/logo.png" style="max-width: 10em; margin-left: 2.5em;"/>
    </div>
    <Dialog :visible="showSpheresModal"
            @update:visible="showSpheresModal = false"
            header="Manage Spheres"
            class="dialog-xl spheres-dialog"
            modal>
      <ScrollPanel class="scroll-panel">
        <Spheres @enterSphere="showSpheresModal = false"></Spheres>
      </ScrollPanel>
    </Dialog>
    <div class="buttons">
      <div v-if="selectedAccountName">
        <strong>Selected Sphere:</strong>
        <Dropdown :options="nestedAccounts"
                  option-label="name" option-value="id"
                  v-model="selectedAccount"
                  class="ml-half"
                  filter
                  :auto-filter-focus="true"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex align-items-center">
              {{ selectedAccountName }}
            </div>
            <span v-else>
              Select a Sphere...
            </span>
          </template>
        </Dropdown>
      </div>
      <template v-if="isAuthenticated">
        <div class="inline-block">
          <Avatar :image="user?.picture" class="mr-half" shape="circle" size="medium"/>
          <div class="user-text inline-block">
            <span class="font-medium">{{ user?.name }}</span>
            <div class="text-sm text-color-secondary">{{ user?.email }}</div>
          </div>
        </div>
        <Button aria-controls="user_options_menu" aria-haspopup="true" icon="pi pi-cog" severity="secondary"
                type="button"
                @click="toggleOptions"
                class="settings-button" />
        <Menu id="user_options_menu" ref="menu" :model="menuItems" :popup="true">
          <template #start>
            <div class="ml-half" v-if="selectedAccount">
              <div class="text-sm bold">
                Selected Sphere:
              </div>
              <div class="text-sm2">
                {{ selectedAccountName }}
              </div>
            </div>
          </template>
          <template #item="{ item, props }">
            <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
              <a :href="href" v-bind="props.action" @click="navigate">
                <i :class="['pi', 'pi-' + item.icon]"/>
                <span>{{ item.label }}</span>
              </a>
            </router-link>
            <a v-else v-bind="props.action">
              <i :class="item.icon"/>
              <span>{{ item.label }}</span>
            </a>
          </template>
        </Menu>
      </template>
      <template v-else>
        <Button @click="loginWithRedirect" class="login-button">
          Log in
        </Button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { useAuth0 } from "@auth0/auth0-vue";
import { computed, ref, unref } from "vue";
import { useAccounts, useUsers } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import Spheres from "../views/Spheres.vue";

const { user, isAuthenticated, loginWithRedirect } = useAuth0();

const { accounts, showAccountDialog, showCreateAccountDialog, selectedAccountName, nestedAccounts } = useAccounts()

const testEnvironment = import.meta.env.MODE !== 'production'

const showSpheresModal = ref(false)

const menu = ref(null)

function toggleOptions(event) {
  if (menu) {
    unref(menu).toggle(event)
  }
}

function navigateToSpheresPage() {
  showSpheresModal.value = true
}

const menuOptions = computed(() => {
  const options = [
    {
      label: 'Manage Spheres',
      icon: 'pi pi-globe',
      command: navigateToSpheresPage
    },
    {
      label: 'Add Sphere',
      icon: 'pi pi-plus',
      command: showCreateAccountDialog
    },
    {
      separator: true
    },
    {
      route: '/logout',
      label: 'Logout',
      icon: 'sign-out'
    },
  ]

  if (accounts.value?.length > 1) {
    options.unshift(
        {
          label: 'Change Sphere',
          icon: 'pi pi-users',
          command: showAccountDialog
        }
    )
  }
  return options
})

const menuItems = ref(menuOptions)

</script>

<style scoped>
.toolbar {
  margin-bottom: 1em;
  padding: 1rem 2rem 1rem 2rem;
  background: #FFFFFF;
  justify-content: space-between;
}

.login-button.button-disabled {
  cursor: not-allowed;
}

@media (max-width: 1023px) {
  .toolbar {
    text-align: center;
  }
  img {
    margin-bottom: .5em;
  }
  .login-button {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .settings-button {
    margin-left: .5rem;
  }
}
@media (min-width: 1024px) {
  .toolbar {
    display: flex;
  }
  .buttons {
    display: flex;
    align-items: center;
  }
  .buttons > * {
    margin-left: .5rem;
    margin-right: .5rem;
  }
}

.user-text {
  margin-left: .25rem;
  font-size: .75rem;
  vertical-align: top;
}

.p-button.p-button-icon-only {
  padding: .5em;
}

:deep(.p-message-wrapper) {
  padding-top: 0;
  padding-bottom: 0;
}
</style>
